import React, { useState, useEffect } from 'react';
import { Tag, Check, X } from 'lucide-react';
import Input from '../common/Input';
import Button from '../common/Button';
import { campaignService } from '../../services/campaignService';
import { useCart } from '../../hooks/useCart';

const CouponField = () => {
  const { coupon, applyCoupon, clearCoupon, getSubtotal } = useCart();
  const [code, setCode] = useState(coupon?.code || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Quando o subtotal muda (ex: removeu/alterou item), revalida o cupom
  // automaticamente. Se ficou inválido, limpa pra evitar inconsistência.
  useEffect(() => {
    if (!coupon?.code) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await campaignService.validate({
          code: coupon.code,
          orderSubtotal: getSubtotal()
        });
        if (cancelled) return;
        if (!result.valid) {
          clearCoupon();
          setError(result.reason || 'Cupom indisponível para esta sacola.');
        } else {
          // Atualiza o discountAmount conforme o subtotal atual.
          applyCoupon({
            code: coupon.code,
            type: result.type,
            discountAmount: result.discountAmount,
            freeShipping: result.freeShipping
          });
        }
      } catch {/* mantém estado atual */}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSubtotal()]);

  const handleApply = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const result = await campaignService.validate({
        code: trimmed,
        orderSubtotal: getSubtotal()
      });
      if (!result.valid) {
        setError(result.reason || 'Cupom inválido.');
        clearCoupon();
        return;
      }
      applyCoupon({
        code: trimmed.toUpperCase(),
        type: result.type,
        discountAmount: result.discountAmount,
        freeShipping: result.freeShipping
      });
      setError('');
    } catch (err) {
      setError(err?.response?.data?.message || 'Falha ao validar cupom.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = () => {
    clearCoupon();
    setCode('');
    setError('');
  };

  if (coupon) {
    return (
      <div className="rounded-lg border border-success/40 bg-success/5 p-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Check size={16} className="text-success shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold text-success truncate">{coupon.code}</p>
            <p className="text-xs text-app-text-secondary">
              {coupon.type === 'FREE_SHIPPING'
                ? 'Frete grátis aplicado'
                : `Desconto aplicado`}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="p-1.5 rounded text-app-text-secondary hover:bg-error/10 hover:text-error transition-colors"
          onClick={handleRemove}
          aria-label="Remover cupom"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-xs font-semibold text-app-text-secondary mb-1.5 uppercase tracking-wide">
        Cupom
      </label>
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Digite seu cupom"
          leftIcon={<Tag size={16} />}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleApply();
            }
          }}
        />
        <Button
          variant="subtle"
          onClick={handleApply}
          loading={loading}
          disabled={!code.trim()}
        >
          Aplicar
        </Button>
      </div>
      {error && <p className="mt-1 text-xs text-error">{error}</p>}
    </div>
  );
};

export default CouponField;
